/* Useful preset rules for validation */

import validator from 'validator';

import { getParsedPhoneNumber } from 'pages/checkout/utils/phoneUtils';

import { ValidationRule, ValidationRuleConfig, ValidationRuleDefinition } from './Validator';

export enum ValidationRulePresetName {
  email = 'email',
  password = 'password',
  phone = 'phone',
  required = 'required',
  insurance = 'insurance',
}

const phoneValidator = (value: string) => {
  const parsedPhoneNumber = getParsedPhoneNumber(value);

  if (!parsedPhoneNumber) {
    return false;
  }

  return parsedPhoneNumber.isValid();
};

export const ValidationRulePreset: Record<ValidationRulePresetName, ValidationRuleDefinition> = {
  email: Object.freeze({
    validatorFunction: validator.isEmail,
    validWhen: true,
    message: 'Invalid email',
  }),
  password: Object.freeze({
    validatorFunction: validator.isLength,
    validWhen: true,
    args: [8, 100],
    message: 'Password must be at least 8 characters',
  }),
  phone: Object.freeze({
    customValidatorFunction: phoneValidator,
    validWhen: true,
    message: 'Invalid phone number',
  }),
  required: Object.freeze({
    validatorFunction: validator.isEmpty,
    validWhen: false,
    message: 'Required',
  }),
  insurance: Object.freeze({
    customValidatorFunction: (value: { id: number, name: string }) => {
      return Boolean(value?.id && value?.name);
    },
    validWhen: true,
    message: 'Required',
  }),
};

/**
 * Create a validation rule from a preset rule
 * @param rule - The preset rule to create a validation rule from
 * @param field - The key (in state) of the field to validate
 * @returns A validation rule
 *
 * @example
 * const formValidator = new FormValidator([
 *   createValidationRule(ValidationRulePreset.email, 'email'),
 *   createValidationRule(ValidationRulePreset.password, 'password'),
 * ]);
 */
export const createValidationRule = <TParams>(config: ValidationRuleConfig<TParams>) => ({
  ...config.preset,
  field: config.field,
  shouldIgnore: config.shouldIgnore,
} as ValidationRule<TParams>);
